import axios from 'axios';

// Base URL for the backend API
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://fnolbackend.alphatorana.com';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
  },
});

// Screen name mapping: Frontend names to API names
const SCREEN_MAPPING = {
  DataHandling: 'Data Handling',
  Validation: 'Validation',
  FraudAnalysis: 'Fraud Analysis',
  Triaging: 'Triaging',
  ClaimsSetup: 'Claims Set Up'
};

// Reverse mapping: API names to Frontend names
const REVERSE_SCREEN_MAPPING = {
  'Data Handling': 'DataHandling',
  'Validation': 'Validation',
  'Fraud Analysis': 'FraudAnalysis',
  'Triaging': 'Triaging',
  'Claims Set Up': 'ClaimsSetup'
};

/**
 * FNOL State Service
 * Manages the state/progress tracking for FNOL workflow screens
 */
class FNOLStateService {
  /**
   * Get current state for FNOL
   * @param {string} fnolId - The FNOL ID
   * @returns {Promise<Object>} State object with frontend screen names
   */
  async getState(fnolId) {
    try {
      const response = await api.get(`/api/fnol/${fnolId}/state`);

      if (response.data.success && response.data.data) {
        // Convert API screen names to frontend names
        const apiState = response.data.data;
        const frontendState = {};

        Object.entries(apiState).forEach(([apiName, status]) => {
          const frontendName = REVERSE_SCREEN_MAPPING[apiName];
          if (frontendName) {
            frontendState[frontendName] = status;
          }
        });

        return frontendState;
      }

      throw new Error('Invalid response format');
    } catch (error) {
      console.error('Failed to fetch FNOL state:', error);
      throw error;
    }
  }

  /**
   * Update specific screen(s) state - RECOMMENDED METHOD
   * @param {string} fnolId - The FNOL ID
   * @param {Object} updates - Object with frontend screen names and their status
   * @returns {Promise<Object>} Updated state with frontend screen names
   */
  async updateState(fnolId, updates) {
    try {
      // Convert frontend screen names to API names
      const apiUpdates = {};
      Object.entries(updates).forEach(([frontendName, status]) => {
        const apiName = SCREEN_MAPPING[frontendName];
        if (apiName) {
          apiUpdates[apiName] = status;
        }
      });

      const response = await api.post(`/api/fnol/${fnolId}/state`, {
        state: apiUpdates
      });

      if (response.data.success && response.data.data) {
        // Convert API screen names back to frontend names
        const apiState = response.data.data;
        const frontendState = {};

        Object.entries(apiState).forEach(([apiName, status]) => {
          const frontendName = REVERSE_SCREEN_MAPPING[apiName];
          if (frontendName) {
            frontendState[frontendName] = status;
          }
        });

        return frontendState;
      }

      throw new Error('Invalid response format');
    } catch (error) {
      console.error('Failed to update FNOL state:', error);
      throw error;
    }
  }

  /**
   * Replace entire state - USE WITH CAUTION
   * @param {string} fnolId - The FNOL ID
   * @param {Object} newState - Complete state object with frontend screen names
   * @returns {Promise<void>}
   */
  async replaceState(fnolId, newState) {
    try {
      // Convert frontend screen names to API names
      const apiState = {};
      Object.entries(newState).forEach(([frontendName, status]) => {
        const apiName = SCREEN_MAPPING[frontendName];
        if (apiName) {
          apiState[apiName] = status;
        }
      });

      const response = await api.put(`/api/fnol/${fnolId}/state`, {
        state: apiState
      });

      if (!response.data.success) {
        throw new Error('Failed to replace state');
      }
    } catch (error) {
      console.error('Failed to replace FNOL state:', error);
      throw error;
    }
  }

  /**
   * Mark screen as visited
   * @param {string} fnolId - The FNOL ID
   * @param {string} screen - Frontend screen name
   * @returns {Promise<Object>} Updated state
   */
  async markAsVisited(fnolId, screen) {
    return this.updateState(fnolId, { [screen]: 'visited' });
  }

  /**
   * Mark screen as completed
   * @param {string} fnolId - The FNOL ID
   * @param {string} screen - Frontend screen name
   * @returns {Promise<Object>} Updated state
   */
  async markAsCompleted(fnolId, screen) {
    return this.updateState(fnolId, { [screen]: 'completed' });
  }

  /**
   * Calculate overall progress percentage
   * @param {Object} state - State object with frontend screen names
   * @returns {number} Progress percentage (0-100)
   */
  calculateProgress(state) {
    if (!state) return 0;

    const screens = Object.values(state);
    const completed = screens.filter(s => s === 'completed').length;
    return (completed / screens.length) * 100;
  }

  /**
   * Calculate weighted progress (visited screens count for 50%)
   * @param {Object} state - State object with frontend screen names
   * @returns {number} Progress percentage (0-100)
   */
  calculateWeightedProgress(state) {
    if (!state) return 0;

    let totalPoints = 0;
    Object.values(state).forEach(status => {
      if (status === 'completed') totalPoints += 100;
      else if (status === 'visited') totalPoints += 50;
      // not_visited = 0 points
    });
    return totalPoints / Object.keys(state).length;
  }

  /**
   * Check if screen can be accessed (previous screen completed)
   * @param {Object} state - State object with frontend screen names
   * @param {string} screen - Frontend screen name to check
   * @returns {boolean} True if screen can be accessed
   */
  canAccessScreen(state, screen) {
    if (!state) return false;

    const screenOrder = ['DataHandling', 'Validation', 'FraudAnalysis', 'Triaging', 'ClaimsSetup'];
    const currentIndex = screenOrder.indexOf(screen);

    if (currentIndex === 0) return true; // First screen always accessible
    if (currentIndex === -1) return false; // Invalid screen name

    const previousScreen = screenOrder[currentIndex - 1];
    return state[previousScreen] === 'completed';
  }

  /**
   * Get the maximum step reached based on state
   * @param {Object} state - State object with frontend screen names
   * @returns {number} Maximum step index (0-4)
   */
  getMaxStepReached(state) {
    if (!state) return 0;

    const screenOrder = ['DataHandling', 'Validation', 'FraudAnalysis', 'Triaging', 'ClaimsSetup'];
    let maxStep = 0;

    screenOrder.forEach((screen, index) => {
      if (state[screen] === 'completed') {
        // If a screen is completed, user can access the next screen
        maxStep = Math.max(maxStep, index + 1);
      } else if (state[screen] === 'visited') {
        // If a screen is visited, user can access that screen
        maxStep = Math.max(maxStep, index);
      }
    });

    // Cap at maximum possible step (4 = last screen)
    return Math.min(maxStep, 4);
  }

  /**
   * Mark all screens as completed (used when FNOL converts to claim)
   * @param {string} fnolId - The FNOL ID
   * @returns {Promise<Object>} Updated state from API
   */
  async markAllAsCompleted(fnolId) {
    try {
      const allCompleted = {
        DataHandling: 'completed',
        Validation: 'completed',
        FraudAnalysis: 'completed',
        Triaging: 'completed',
        ClaimsSetup: 'completed'
      };

      const updatedState = await this.updateState(fnolId, allCompleted);
      console.log(`✅ All pages marked as completed for FNOL ${fnolId}`);
      return updatedState;
    } catch (error) {
      console.error('Failed to mark all pages as completed:', error);
      throw error;
    }
  }
}

// Export singleton instance
const fnolStateService = new FNOLStateService();
export default fnolStateService;
